/**
 * Main Middleware Client
 * Unified HTTP client for QwickForge middleware-service
 */
import { getLogger } from '@qwickapps/logging';
import { HttpClient } from './HttpClient.js';
import { SessionsResource } from '../resources/SessionsResource.js';
import { PromptsResource } from '../resources/PromptsResource.js';
import { ExecutionsResource } from '../resources/ExecutionsResource.js';
import { AgentsResource } from '../resources/AgentsResource.js';
import { ProjectsResource } from '../resources/ProjectsResource.js';
import { ToolUsageResource } from '../resources/ToolUsageResource.js';
import { ProfileResource } from '../resources/ProfileResource.js';
import { OrganizationsResource } from '../resources/OrganizationsResource.js';
import { ShorthandsResource } from '../resources/ShorthandsResource.js';
import { DevicesResource } from '../resources/DevicesResource.js';
import { PairingResource } from '../resources/PairingResource.js';
import { SystemSettingsResource } from '../resources/SystemSettingsResource.js';
import { SubscriptionResource } from '../resources/SubscriptionResource.js';
import { WriteBuffer } from '../features/WriteBuffer.js';
import { HealthMonitor } from '../features/HealthMonitor.js';
const log = getLogger('QwickForge.MiddlewareClient');
export class MiddlewareClient {
    // Resource accessors (REST API endpoints)
    sessions;
    prompts;
    executions;
    agents;
    projects;
    toolUsage;
    profile;
    organizations;
    shorthands;
    devices;
    pairing;
    systemSettings;
    subscription;
    // Internal components
    httpClient;
    healthMonitor;
    writeBuffer;
    authProvider;
    config;
    constructor(config, authProvider) {
        this.config = config;
        this.authProvider = authProvider;
        log.debug('Initializing MiddlewareClient', {
            baseUrl: config.baseUrl,
            authType: authProvider.getAuthType(),
            identifier: authProvider.getIdentifier?.() || 'unknown',
            features: {
                writeBuffer: config.enableWriteBuffer ?? false,
                healthMonitor: config.enableHealthMonitor ?? false,
                retry: config.enableRetry ?? false,
            },
        });
        // Validate auth
        if (!authProvider.hasCredentials()) {
            log.warn('Auth provider has no credentials - requests may fail');
        }
        // Initialize HTTP client
        this.httpClient = new HttpClient({
            baseUrl: config.baseUrl,
            getAuthHeaders: () => authProvider.getAuthHeaders(),
            retryOptions: config.enableRetry ? config.retryOptions : undefined,
            logger: config.logger,
        });
        // Initialize write buffer if enabled
        if (config.enableWriteBuffer) {
            const bufferConfig = config.bufferOptions || {
                maxBatchSize: 10,
                maxBatchDelay: 1000,
            };
            this.writeBuffer = new WriteBuffer(bufferConfig, this.httpClient);
            log.debug('Write buffer enabled', bufferConfig);
        }
        // Initialize resources
        this.sessions = new SessionsResource(this.httpClient);
        this.prompts = new PromptsResource(this.httpClient, this.writeBuffer);
        this.executions = new ExecutionsResource(this.httpClient, this.writeBuffer);
        this.agents = new AgentsResource(this.httpClient);
        this.projects = new ProjectsResource(this.httpClient);
        this.toolUsage = new ToolUsageResource(this.httpClient, this.writeBuffer);
        this.profile = new ProfileResource(this.httpClient);
        this.organizations = new OrganizationsResource(this.httpClient);
        this.shorthands = new ShorthandsResource(this.httpClient);
        this.devices = new DevicesResource(this.httpClient);
        this.pairing = new PairingResource(this.httpClient);
        this.systemSettings = new SystemSettingsResource(this.httpClient);
        this.subscription = new SubscriptionResource(this.httpClient);
        // Initialize health monitor if enabled
        if (config.enableHealthMonitor) {
            const interval = config.healthCheckInterval || 30000;
            this.healthMonitor = new HealthMonitor(this.httpClient, interval);
            this.healthMonitor.start();
            log.debug('Health monitor enabled', { interval });
        }
        log.debug('MiddlewareClient initialized successfully');
    }
    /**
     * Force flush write buffer
     */
    async flush() {
        if (this.writeBuffer) {
            await this.writeBuffer.flush();
        }
    }
    /**
     * Get service health status
     */
    getHealth() {
        if (this.healthMonitor) {
            return this.healthMonitor.getStatus();
        }
        // Return default healthy status if monitoring disabled
        return {
            isReachable: true,
            consecutiveFailures: 0,
        };
    }
    /**
     * Force a health check
     */
    async checkHealth() {
        if (this.healthMonitor) {
            return await this.healthMonitor.forceCheck();
        }
        // Perform one-time health check
        const startTime = Date.now();
        try {
            await this.httpClient.get('/health');
            const latencyMs = Date.now() - startTime;
            return {
                isReachable: true,
                consecutiveFailures: 0,
                lastSuccessfulRequest: new Date(),
                latencyMs,
            };
        }
        catch (error) {
            const latencyMs = Date.now() - startTime;
            log.error('Health check failed', {
                error: error instanceof Error ? error.message : String(error),
            });
            return {
                isReachable: false,
                consecutiveFailures: 1,
                lastFailedRequest: new Date(),
                latencyMs,
            };
        }
    }
    /**
     * Get client configuration
     */
    getConfig() {
        return { ...this.config };
    }
    /**
     * Get auth provider
     */
    getAuthProvider() {
        return this.authProvider;
    }
    /**
     * Close client and cleanup resources
     */
    async close() {
        log.info('Closing MiddlewareClient');
        // Stop health monitoring
        if (this.healthMonitor) {
            this.healthMonitor.stop();
        }
        // Flush and close write buffer
        if (this.writeBuffer) {
            await this.writeBuffer.close();
        }
        log.info('MiddlewareClient closed');
    }
}
//# sourceMappingURL=MiddlewareClient.js.map